// ============================================================
// TMR Portal Admin — the authorization audit log (10 B7c)
// Reached only with authorization.admin — see nav.ts/Portal.
// ============================================================
import { useEffect, useState } from 'react';
import { pageCount } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { PageHeader, Table, Td, Drawer, Field } from '../../components/ui';
import { CopyableText } from '../../components/CopyableText';
import { Pagination } from '../../components/Pagination';
import { RecordPicker } from '../../components/RecordPicker';
import { SelectField } from '../../components/fields';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useUserRef, useUserSearch } from '../../data/users/queries';
import { changedFields, formatAuditValue } from '../../data/authorization/audit';
import { AUDIT_ENTITIES, auditActionLabel, auditEntityLabel, auditFieldLabel } from '../../data/authorization/labels';
import { auditLogExportPage, useAuditLog } from '../../data/authorization/queries';
import { ExportMenu } from '../../components/ExportMenu';
import { AUDIT_LOG_EXPORT_COLUMNS } from './exportColumns';
import type { AuditLogEntry } from '../../data/authorization/contract';
import type { UserOption } from '../../data/users/contract';

/**
 * Every change to roles, permissions and assignments, in the order it happened. Read-only by design —
 * the log is evidence, and nothing on this screen writes.
 *
 * The interceptor records whole snapshots (10 B7c), so each entry is shown as a **diff** of the two: an
 * `updated` Role is a dozen identical values around the one that moved, and the blob is not what anyone
 * came to read. The full JSON stays one click away for when it is.
 */
export function AuditLogPage() {
  const canManageUsers = usePermission(PERMISSIONS.usersManage);
  const [entity, setEntity] = useState('');
  const [actor, setActor] = useState<UserOption | null>(null);
  const [page, setPage] = useState(1);
  const [picking, setPicking] = useState(false);
  const [inspecting, setInspecting] = useState<AuditLogEntry | null>(null);

  // Described once: the table pages through these filters and the export walks all of them.
  const filters = { entity: entity || undefined, actorUserId: actor?.id };
  const log = useAuditLog({ ...filters, page });

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  // The log only grows, so a page rarely disappears — but "rarely" is not "never" (an actor filter that
  // matches fewer entries after a narrowing does it), and an empty page with no pagination control is a
  // dead end either way.
  // Never below 1: an empty result has a page count of zero, and page 0 is not a page the backend has.
  const pages = Math.max(1, log.data ? pageCount(log.data) : 1);
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  // While a filter change is in flight the previous filter's rows stay on screen (`placeholderData`).
  // This is an evidentiary screen: presenting unfiltered rows as the filtered result, with no signal, is
  // the wrong trade — so the table says it is not answering the question on screen yet.
  const stale = log.isPlaceholderData && log.isFetching;

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Autorização"
        title="Auditoria"
        desc="Toda alteração de papéis e acessos, na ordem em que aconteceu."
        actions={
          <ExportMenu
            spec={{
              fileName: 'auditoria',
              columns: AUDIT_LOG_EXPORT_COLUMNS,
              fetchPage: auditLogExportPage(filters),
            }}
            totalCount={log.data?.totalCount ?? 0}
            disabled={log.isPending}
          />
        }
      />

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 240 }}>
          <SelectField
            label="Tipo de registro"
            value={entity}
            onChange={reset(setEntity)}
            options={AUDIT_ENTITIES.map((name) => ({ value: name, label: auditEntityLabel(name) }))}
            placeholder="Todos"
          />
        </div>
        {canManageUsers && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Btn variant="secondary" icon={Icons.users} onClick={() => setPicking(true)}>
              {actor ? actor.name : 'Filtrar por quem alterou'}
            </Btn>
            {actor && (
              <button type="button" onClick={() => reset(setActor)(null)} className="pa-icon-btn" style={{ width: 30, height: 30 }} title="Limpar filtro">
                <StrokeIcon d={Icons.x} size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {log.isPending ? (
        <Skeleton rows={8} />
      ) : log.isError ? (
        <ErrorState error={log.error} onRetry={() => void log.refetch()} />
      ) : log.data.items.length === 0 ? (
        <>
          <EmptyState
            message="Nenhuma alteração registrada."
            hint={entity || actor ? 'Ajuste os filtros para ver o restante do histórico.' : 'O registro começa na primeira alteração de autorização.'}
          />
          {/* Outside the non-empty branch on purpose: an empty page is when the operator most needs the
              control that takes them back to one that is not. */}
          <Pagination
            page={log.data.page}
            pageCount={pageCount(log.data)}
            total={log.data.totalCount}
            onChange={setPage}
          />
        </>
      ) : (
        <div aria-busy={stale} style={{ opacity: stale ? 0.55 : 1, transition: 'opacity 120ms var(--ease-out)' }}>
          <Table
            dense
            columns={[
              { label: 'Quando', width: 160 },
              { label: 'O quê' },
              { label: 'Quem' },
              { label: '', align: 'right', width: 110 },
            ]}
          >
            {log.data.items.map((entry) => (
              <tr key={entry.id} className="pa-row">
                <Td dense>
                  <span style={{ fontSize: 13 }}>{formatInstant(entry.at)}</span>
                </Td>
                <Td dense>
                  <div style={{ fontSize: 13.5 }}>
                    {auditEntityLabel(entry.entity)} · {auditActionLabel(entry.action)}
                  </div>
                  {entry.entityId && (
                    <CopyableText value={entry.entityId} style={{ fontSize: 11, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }} />
                  )}
                </Td>
                <Td dense>
                  <ActorCell actorUserId={entry.actorUserId} canResolve={canManageUsers} />
                </Td>
                <Td dense align="right">
                  <button type="button" onClick={() => setInspecting(entry)} className="pa-link-btn" style={{ fontSize: 13 }}>
                    Ver detalhe
                  </button>
                </Td>
              </tr>
            ))}
          </Table>
          <Pagination
            page={log.data.page}
            pageCount={pageCount(log.data)}
            total={log.data.totalCount}
            onChange={setPage}
          />
        </div>
      )}

      <Drawer
        open={Boolean(inspecting)}
        onClose={() => setInspecting(null)}
        title={inspecting ? `${auditEntityLabel(inspecting.entity)} · ${auditActionLabel(inspecting.action)}` : ''}
        width={520}
      >
        {inspecting && <AuditDetail entry={inspecting} canResolveActor={canManageUsers} />}
      </Drawer>

      {picking && (
        <RecordPicker<UserOption>
          eyebrow="Auditoria"
          title="Filtrar por quem alterou"
          subtitle="Mostra apenas as alterações feitas por esta pessoa."
          placeholder="Nome ou e-mail do colaborador…"
          useSearch={useUserSearch}
          getKey={(user) => user.id}
          toRow={(user) => ({ primary: user.name, secondary: user.email })}
          onSelect={reset(setActor)}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

/** Props for {@link AuditDetail}. */
interface AuditDetailProps {
  entry: AuditLogEntry;
  canResolveActor: boolean;
}

/**
 * One entry, read as a change.
 *
 * The log stores whole snapshots, so the fields that stayed the same outnumber the one that moved. The
 * diff answers the question the operator actually has; the full snapshots stay one click away for when
 * it is the whole row that matters.
 */
function AuditDetail({ entry, canResolveActor }: AuditDetailProps) {
  const [showRaw, setShowRaw] = useState(false);
  const changes = changedFields(entry.before, entry.after);

  return (
    <>
      <Field label="Quando">{formatInstant(entry.at)}</Field>
      <Field label="Quem">
        <ActorCell actorUserId={entry.actorUserId} canResolve={canResolveActor} />
      </Field>
      <Field label="Registro">{entry.entityId ?? '—'}</Field>

      <div style={{ marginTop: 18 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 8 }}>O que mudou</div>
        {changes.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-subtle)' }}>
            Nenhum valor diferente entre os dois estados.
          </p>
        ) : (
          <Table dense columns={[{ label: 'Campo' }, { label: 'De' }, { label: 'Para' }]}>
            {changes.map((change) => (
              <tr key={change.field}>
                <Td dense>{auditFieldLabel(change.field)}</Td>
                <Td dense style={{ color: 'var(--fg-muted)' }}>{formatAuditValue(change.before)}</Td>
                <Td dense>{formatAuditValue(change.after)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <button type="button" onClick={() => setShowRaw((current) => !current)} className="pa-link-btn" style={{ fontSize: 13 }}>
          {showRaw ? 'Ocultar registro completo' : 'Ver registro completo'}
        </button>
        {showRaw && (
          <pre style={{ marginTop: 10, padding: 12, background: 'var(--cream-100)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-mono)', fontSize: 11.5, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
          </pre>
        )}
      </div>
    </>
  );
}

/** Props for {@link ActorCell}. */
interface ActorCellProps {
  actorUserId: string | null;
  /** Whether the operator may read the user directory at all (`users.manage`). */
  canResolve: boolean;
}

/**
 * Who made the change.
 *
 * The audit log records only the actor's id, so the name is resolved here, one request per distinct
 * actor and cached. Without `users.manage` there is no directory to read and the id is shown as-is —
 * which is still the truth, just less readable. A write with no actor came from the system itself
 * (the admin bootstrap on first login), and says so.
 */
function ActorCell({ actorUserId, canResolve }: ActorCellProps) {
  const user = useUserRef(actorUserId, canResolve);

  if (!actorUserId) return <span style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>sistema</span>;
  if (user.data) return <span style={{ fontSize: 13.5 }}>{user.data.name}</span>;

  return (
    <span style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--fg-subtle)' }}>{actorUserId}</span>
  );
}

/** An ISO instant as the operator reads it, in their own time zone. */
function formatInstant(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
